import { createSlice } from "@reduxjs/toolkit";
import type { Asset } from "../models/Asset";
import { INITIAL_VALUES } from "../models/InitialValueTemplate";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import type { Drawing } from "../components/template-resources/models/TemplateDrawing";
import type { TemplateLOTO } from "../components/template-resources/models/TemplateLOTO";
import type { TemplatePart } from "../components/template-resources/models/TemplatePart";
import { METADATA_INITIAL_VALUES } from "@/pages/drawings/search-documents/models/metaDataInitialValue";
import type { DocumentParameters } from "@/pages/drawings/search-documents/models/DocumentParameters";
import type { Attachments } from "@/pages/asset-details/models/Attachment";
import type { LOTOResource } from "@/pages/asset-details/models/LOTOResource";
import type { Parts } from "@/pages/drawings/search-documents/models/Parts";
import type { TemplateAttachment } from "../components/template-resources/models/TemplateAttachment";
import type { Template } from "../models/Template";

const TemplatesReducerInitialState: {
	INITIAL_VALUE_TEMPLATE: ParametersNewTemplate;
	INITIAL_VALUE_TEMPLATE_SELECTED: ParametersNewTemplate;
	searchAssetIdToTable: number | string;
	searchAssetName: string;
	searchText: string;
	rowData: any;
	rows: any;
	noResults: boolean;
	allAssets: Asset[];
	firstTime: boolean;
	templateIdToDelete: number | null;
	rowSelectedTemplate: Template;
	listAllDrawings: Drawing[];
	templateDrawingToAdd: any;
	templateLOTOToAdd: any;
	templateAttachmentToAdd: any;
	assetDrawings: Drawing[];
	assetAttachments: Attachments[] | TemplateAttachment[];
	assetLOTO: LOTOResource[] | TemplateLOTO[];
	assetParts: Parts[];
	drawingSearch: string;
	showListOfDrawings: boolean;
	loaderListSearchDrawings: boolean;
	dataDrawingSelected: Drawing | null;
	dataLOTOSelected: TemplateLOTO | null;
	dataAttachmentSelected: TemplateAttachment | null;
	dataPartSelected: TemplatePart | null;
	drawingsDocumentMetadata: DocumentParameters;
	rowSelectedInDrawing: any;
	rowSelectedInLOTO: any;
	rowSelectedInAttachment: any;
	rowSelectedInPart: any;
	LOTONumberToLink: string;
	LOTONameToLink: string;
	displayViewerAttachment: boolean;
	listAttachmentSelected: any[] | null;
	listDrawingsSelected: any[] | null;
	listPartsSelected: any[] | null;
	listLOTOSelected: any[] | null;
	uploadFileDialog: boolean;
	templateDrawingsList: Drawing[];
	templateAttachmentsList: TemplateAttachment[];
	templateLOTOList: TemplateLOTO[];
	templatePartsList: TemplatePart[];
	listAllParts: TemplatePart[];
	templatePartsToAdd: any;
	partSearch: string;
	showListOfParts: boolean;
	loaderListSearchParts: boolean;
	isCreatingLOTO: boolean;
} = {
	INITIAL_VALUE_TEMPLATE: INITIAL_VALUES,
	INITIAL_VALUE_TEMPLATE_SELECTED: INITIAL_VALUES,
	searchAssetIdToTable: 0,
	searchAssetName: "",
	rowData: [],
	rows: [],
	searchText: "",
	noResults: false,
	allAssets: [],
	firstTime: true,
	templateIdToDelete: null,
	rowSelectedTemplate: {
		TemplateId: null,
		TypeCode: null,
		Title: "",
		AssetId: null,
		CraftId: null,
		PeriodInDays: null,
		ExpectedShifts: null,
		WarningShifts: null,
		DownHours: null,
		DowntimeClassCode: null,
		PlannedHours: null,
		Description: null,
		NumberOfWorkers: null,
		StatusCode: null,
		CreatedTimestamp: null,
		CreatedBy: null,
		PeriodShifts: null,
		AssetName: null,
		CraftName: null,
		CraftColor: null,
		ScheduleStart: null,
		SchedulingPolicyCode: "",
		CronExpression: "",
	},
	assetDrawings: [],
	assetAttachments: [],
	assetLOTO: [],
	assetParts: [],
	rowSelectedInDrawing: null,
	listAllDrawings: [],
	templateDrawingsList: [],
	dataDrawingSelected: null,
	templateDrawingToAdd: [],
	drawingsDocumentMetadata: METADATA_INITIAL_VALUES,
	drawingSearch: "",
	showListOfDrawings: false,
	loaderListSearchDrawings: false,
	templateLOTOToAdd: {},
	rowSelectedInLOTO: [],
	templateLOTOList: [],
	dataLOTOSelected: null,
	LOTONumberToLink: "",
	LOTONameToLink: "",
	isCreatingLOTO: false,
	templateAttachmentToAdd: {},
	rowSelectedInAttachment: [],
	templateAttachmentsList: [],
	dataAttachmentSelected: null,
	displayViewerAttachment: false,
	listAttachmentSelected: [],
	listDrawingsSelected: [],
	listPartsSelected: [],
	listLOTOSelected: [],
	uploadFileDialog: false,
	rowSelectedInPart: [],
	listAllParts: [],
	templatePartsList: [],
	dataPartSelected: null,
	templatePartsToAdd: [],
	partSearch: "",
	showListOfParts: false,
	loaderListSearchParts: false,
};

export const TemplatesReducer = createSlice({
	name: "__",
	initialState: TemplatesReducerInitialState,
	reducers: {
		setAssetId(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE.AssetId = payload;
		},
		setShiftsPerDay(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE.shiftsPerDay = payload;
		},
		setPeriodDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE.selectedPeriodDays = payload;
		},
		setWarningDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE.selectedWarningDays = payload;
		},
		setExpectedDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE.selectedExpectedDays = payload;
		},
		setRowSelectedTemplate(state, { payload }) {
			state.rowSelectedTemplate = payload;
		},
		setSearchAssetIdToTable(state, { payload }) {
			state.searchAssetIdToTable = payload;
		},
		setSearchAssetName(state, { payload }) {
			state.searchAssetName = payload;
		},
		setSearchText(state, { payload }) {
			state.searchText = payload;
		},
		setRows(state, { payload }) {
			state.rows = payload;
		},
		setRowData(state, { payload }) {
			state.rowData = payload;
		},
		setNoResults(state, { payload }) {
			state.noResults = payload;
		},
		setallAssets(state, { payload }) {
			state.allAssets = payload;
		},
		setSelectedTemplate(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED = payload;
		},
		setSelectedTemplateId(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.TemplateId = payload;
		},
		setSelectedAssetId(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.AssetId = payload;
		},
		setSelectedTypeCode(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.TypeCode = payload;
		},
		setSelectedTitle(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.Title = payload;
		},
		setSelectedCraftId(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.CraftId = payload;
		},
		setSelectedDownHours(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.DownHours = payload;
		},
		setSelectedPlannedHours(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.PlannedHours = payload;
		},
		setSelectedDowntimeClassCode(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.DowntimeClassCode = payload;
		},
		setSelectedNumberOfWorkers(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.NumberOfWorkers = payload;
		},
		setSelectedDescription(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.Description = payload;
		},
		setSelectedExpectedShifts(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.ExpectedShifts = payload;
		},
		setSelectedWarningShifts(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.WarningShifts = payload;
		},
		setSelectedPeriodShifts(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.PeriodShifts = payload;
		},
		setSelectedAssetName(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.AssetName = payload;
		},
		setSelectedShiftsPerDay(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.shiftsPerDay = payload;
		},
		setSelectedPeriodDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.selectedPeriodDays = payload;
		},
		setSelectedWarningDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.selectedWarningDays = payload;
		},
		setSelectedExpectedDays(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.selectedExpectedDays = payload;
		},
		setSelectedSchedulingPolicyCode(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.SchedulingPolicyCode = payload;
		},
		setSelectedScheduleStart(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.ScheduleStart = payload;
		},
		setSelectedCronExpression(state, { payload }) {
			state.INITIAL_VALUE_TEMPLATE_SELECTED.selectedCronExpression = payload;
		},
		setTemplateIdToDelete(state, { payload }) {
			state.templateIdToDelete = payload;
		},
		setFirstTime(state, { payload }) {
			state.firstTime = payload;
		},
		setAssetDrawings(state, { payload }) {
			state.assetDrawings = payload;
		},
		setAssetLOTOList(state, { payload }) {
			state.assetLOTO = payload;
		},
		setAssetAttachmentsList(state, { payload }) {
			state.assetAttachments = payload;
		},
		setAssetPartsList(state, { payload }) {
			state.assetParts = payload;
		},
		setListAllDrawings(state, { payload }) {
			state.listAllDrawings = payload;
		},
		setTemplateDrawingsList(state, { payload }) {
			state.templateDrawingsList = payload;
		},
		setTemplateDrawingToAdd(state, { payload }) {
			state.templateDrawingToAdd = payload;
		},
		setShowListOfDrawings(state, { payload }) {
			state.showListOfDrawings = payload;
		},
		setLoaderListSearchDrawings(state, { payload }) {
			state.loaderListSearchDrawings = payload;
		},
		setDrawingSearch(state, { payload }) {
			state.drawingSearch = payload;
		},
		setDataDrawingSelected(state, { payload }) {
			state.dataDrawingSelected = payload;
		},
		setListDrawingsSelected(state, { payload }) {
			state.listDrawingsSelected = payload;
		},
		setDocumentMetadata(state, { payload }) {
			state.drawingsDocumentMetadata = payload;
		},
		setVersionDocumentMetadata(state, { payload }) {
			state.drawingsDocumentMetadata.Version = payload;
		},
		setExtensionDocumentMetadata(state, { payload }) {
			state.drawingsDocumentMetadata.Extension = payload;
		},
		setUploadTimestampDocumentMetadata(state, { payload }) {
			state.drawingsDocumentMetadata.UploadTimestamp = payload;
		},
		setUserNameDocumentMetadata(state, { payload }) {
			state.drawingsDocumentMetadata.UserName = payload;
		},
		setRowSelectedInDrawing(state, { payload }) {
			state.rowSelectedInDrawing = payload;
		},
		setDocumentIdRowSelected(state, { payload }) {
			state.rowSelectedInDrawing.DocumentId = payload;
		},
		setTemplateAttachmentsList(state, { payload }) {
			state.templateAttachmentsList = payload;
		},
		setTemplateAttachmentToAdd(state, { payload }) {
			state.templateAttachmentToAdd = payload;
		},
		setRowSelectedInAttachment(state, { payload }) {
			state.rowSelectedInAttachment = payload;
		},
		setDataAttachmentSelected(state, { payload }) {
			state.dataAttachmentSelected = payload;
		},
		setDisplayViewerAttachment(state, { payload }) {
			state.displayViewerAttachment = payload;
		},
		setListAttachmentSelected(state, { payload }) {
			state.listAttachmentSelected = payload;
		},
		setUploadFileDialog(state, { payload }) {
			state.uploadFileDialog = payload;
		},
		setTemplateLOTOList(state, { payload }) {
			state.templateLOTOList = payload;
		},
		setDataLOTOSelected(state, { payload }) {
			state.dataLOTOSelected = payload;
		},
		setRowSelectedInLOTO(state, { payload }) {
			state.rowSelectedInLOTO = payload;
		},
		setListLOTOSelected(state, { payload }) {
			state.listLOTOSelected = payload;
		},
		setLOTONumberToLink(state, { payload }) {
			state.LOTONumberToLink = payload;
		},
		setLOTONameToLink(state, { payload }) {
			state.LOTONameToLink = payload;
		},
		setTemplateLOTOToAdd(state, { payload }) {
			state.templateLOTOToAdd = payload;
		},
		setIsCreatingLOTO(state, { payload }) {
			state.isCreatingLOTO = payload;
		},
		setListAllParts(state, { payload }) {
			state.listAllParts = payload;
		},
		setTemplatePartsList(state, { payload }) {
			state.templatePartsList = payload;
		},
		setTemplatePartsToAdd(state, { payload }) {
			state.templatePartsToAdd = payload;
		},
		setShowListOfParts(state, { payload }) {
			state.showListOfParts = payload;
		},
		setListPartsSelected(state, { payload }) {
			state.listPartsSelected = payload;
		},
		setLoaderListSearchParts(state, { payload }) {
			state.loaderListSearchParts = payload;
		},
		setPartSearch(state, { payload }) {
			state.drawingSearch = payload;
		},
		setDataPartSelected(state, { payload }) {
			state.dataPartSelected = payload;
		},
		setAssetParts(state, { payload }) {
			state.assetParts = payload;
		},
		setRowSelectedInPart(state, { payload }) {
			state.rowSelectedInPart = payload;
		},
		setPartIdRowSelected(state, { payload }) {
			state.rowSelectedInPart.DocumentId = payload;
		},
	},
});
